/**
 * Sign in, MFA, and invitation redemption.
 *
 * One screen handling four states, because they are one journey: password →
 * possibly a code → possibly enrolling in two-factor → signed in. Splitting them
 * across routes would mean carrying half-authenticated state through the URL.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError, tokenStore } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { APP_TAGLINE, useAppName } from '../lib/branding.ts';
import { BrandMark } from '../components/BrandMark.tsx';
import { Banner, Button, Field, Input, Spinner } from '../components/ui.tsx';

type Stage = 'password' | 'mfa' | 'enrol';

interface SignInResponse {
  status: 'signed_in' | 'mfa_required' | 'mfa_enrolment_required';
  token?: string;
  message?: string;
  enrolmentToken?: string;
  secret?: string;
  otpauthUri?: string;
}

export function SignIn() {
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [params] = useSearchParams();

  const [stage, setStage] = useState<Stage>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [enrolment, setEnrolment] = useState<{ token: string; secret: string; uri: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Where to go after signing in — preserves a deep link from an approval email. */
  const next = params.get('next') ?? '/';

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setHint(null);

    try {
      if (stage === 'enrol' && enrolment) {
        const result = await api.post<{ token: string }>('/api/auth/mfa/confirm', {
          enrolmentToken: enrolment.token,
          secret: enrolment.secret,
          code,
        });
        tokenStore.set(result.token);
        await refresh();
        navigate(next, { replace: true });
        return;
      }

      const result = await api.post<SignInResponse>('/api/auth/sign-in', {
        email,
        password,
        totpCode: stage === 'mfa' ? code : undefined,
      });

      if (result.status === 'signed_in' && result.token) {
        tokenStore.set(result.token);
        await refresh();
        navigate(next, { replace: true });
        return;
      }

      if (result.status === 'mfa_required') {
        setStage('mfa');
        setHint(result.message ?? null);
        return;
      }

      if (result.status === 'mfa_enrolment_required' && result.enrolmentToken) {
        setEnrolment({
          token: result.enrolmentToken,
          secret: result.secret ?? '',
          uri: result.otpauthUri ?? '',
        });
        setStage('enrol');
        setHint(result.message ?? null);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign in. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
      <form onSubmit={submit} className="space-y-4" noValidate>
        {stage === 'password' && (
          <>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-[var(--ink-primary)]">Sign in</h1>
              <p className="mt-1 text-[13px] text-[var(--ink-secondary)]">
                Use your work email address.
              </p>
            </div>

            <Field label="Email address" required>
              <Input
                type="email"
                autoComplete="username"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@school.org"
              />
            </Field>

            <Field label="Password" required>
              <Input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
          </>
        )}

        {stage === 'mfa' && (
          <>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-[var(--ink-primary)]">
                Enter your code
              </h1>
              <p className="mt-1 text-[13px] text-[var(--ink-secondary)]">
                Open your authenticator app and enter the six-digit code for {email}.
              </p>
            </div>
            <Field label="Six-digit code" required hint="Codes change every 30 seconds.">
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                autoFocus
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-lg tracking-[0.3em] tabular"
              />
            </Field>
          </>
        )}

        {stage === 'enrol' && enrolment && (
          <>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-[var(--ink-primary)]">
                Set up two-factor authentication
              </h1>
              <p className="mt-1 text-[13px] text-[var(--ink-secondary)]">
                You can approve spending, so this is required.
              </p>
            </div>

            <ol className="space-y-2 text-[13px] text-[var(--ink-secondary)]">
              <li>1. Open an authenticator app — Microsoft Authenticator, Google Authenticator, or 1Password.</li>
              <li>2. Scan this square with it.</li>
            </ol>

            <QrCode uri={enrolment.uri} secret={enrolment.secret} />

            <Field label="Then enter the six-digit code it shows" required>
              <Input
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoFocus
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-lg tracking-[0.3em] tabular"
              />
            </Field>
          </>
        )}

        {hint && <Banner tone="info">{hint}</Banner>}
        {error && <Banner tone="danger">{error}</Banner>}

        <Button type="submit" variant="primary" size="lg" full loading={busy}>
          {stage === 'password' ? 'Sign in' : 'Continue'}
        </Button>

        {stage !== 'password' && (
          <Button
            type="button"
            variant="ghost"
            full
            onClick={() => {
              setStage('password');
              setCode('');
              setError(null);
              setHint(null);
            }}
          >
            Start again
          </Button>
        )}
      </form>
    </AuthLayout>
  );
}

// ---------------------------------------------------------------------------
// Invitation
// ---------------------------------------------------------------------------

export function AcceptInvitation() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (password !== confirm) {
      setError('Those two passwords do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ message: string }>('/api/auth/accept-invitation', {
        token,
        password,
      });
      setDone(result.message);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not set your password.');
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout>
        <Banner tone="danger" title="That link is incomplete">
          Ask your administrator to send a new invitation.
        </Banner>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout>
        <div className="space-y-4">
          <Banner tone="success" title="Password set">
            {done}
          </Banner>
          <Link to="/sign-in">
            <Button variant="primary" size="lg" full>
              Go to sign in
            </Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <form onSubmit={submit} className="space-y-4" noValidate>
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-[var(--ink-primary)]">
            Choose a password
          </h1>
          <p className="mt-1 text-[13px] text-[var(--ink-secondary)]">
            This link works once and then expires.
          </p>
        </div>

        <Field
          label="New password"
          required
          hint="At least 12 characters. Three unrelated words is easier to remember and harder to guess than a short complicated one."
        >
          <Input
            type="password"
            autoComplete="new-password"
            autoFocus
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>

        <Field label="Confirm password" required>
          <Input
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>

        {error && <Banner tone="danger">{error}</Banner>}

        <Button type="submit" variant="primary" size="lg" full loading={busy}>
          Set password
        </Button>
      </form>
    </AuthLayout>
  );
}

// ---------------------------------------------------------------------------

function AuthLayout({ children }: { children: React.ReactNode }) {
  const appName = useAppName();

  return (
    <div className="grid min-h-dvh lg:grid-cols-2">
      {/* Left panel is decorative on desktop and hidden on mobile, where the form
          should be the only thing on screen. */}
      <div className="hidden flex-col justify-between bg-navy-900 p-10 text-white lg:flex">
        <div className="flex items-center gap-2.5 font-semibold">
          <BrandMark appName={appName} className="size-8" />
          {appName}
        </div>

        <div className="max-w-md">
          <h2 className="text-2xl font-semibold leading-snug tracking-tight">
            {APP_TAGLINE}
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-navy-200">
            Every request follows the same approval route, every approval is recorded, and
            budgets update the moment something is approved rather than when the invoice
            arrives.
          </p>
        </div>

        <p className="text-[12px] text-navy-300">
          Approving spending? You will be asked to set up two-factor authentication.
        </p>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">{children}</div>
      </div>
    </div>
  );
}

/**
 * The two-factor enrolment code.
 *
 * Scanning is the path almost everyone takes, and typing a 32-character key on a
 * phone keypad is where enrolment goes wrong — so the square is the headline and the
 * key is kept as the fallback for anyone setting up an authenticator on the same
 * device they are reading this on.
 *
 * The encoder is imported only when this step is reached. It is ~40 kB and the
 * approval screen — the one opened from a phone on a Sunday evening — must not pay
 * for it.
 *
 * Rendered as an inline SVG data URI rather than a canvas so it survives the
 * artifact-style content policies that block remote images, and prints cleanly.
 */
function QrCode({ uri, secret }: { uri: string; secret: string }) {
  const [svg, setSvg] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!uri) {
      setFailed(true);
      return;
    }
    import('qrcode')
      .then((qr) =>
        qr.toString(uri, {
          type: 'svg',
          margin: 1,
          // Medium error correction: enough that a slightly glared phone screen
          // still reads, without inflating the module count.
          errorCorrectionLevel: 'M',
          color: { dark: '#0f172a', light: '#ffffff' },
        }),
      )
      .then((out) => !cancelled && setSvg(out))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [uri]);

  const key = secret.match(/.{1,4}/g)?.join(' ') ?? secret;

  return (
    <div className="space-y-2">
      {/* Always white, in both themes — a dark-inverted QR code is unreliable to scan. */}
      <div className="rounded-md border border-[var(--border-default)] bg-white p-3">
        {svg ? (
          <img
            src={`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`}
            alt="QR code for setting up two-factor authentication"
            className="mx-auto size-40"
          />
        ) : failed ? (
          <p className="py-2 text-center text-[12px] text-navy-900">
            The code could not be drawn. Use the key below instead — it does the same thing.
          </p>
        ) : (
          <div className="grid h-40 place-items-center">
            <Spinner className="size-5 text-navy-700" />
          </div>
        )}
      </div>

      {showKey || failed ? (
        <div className="rounded-md border border-[var(--border-default)] bg-[var(--surface-2)] p-3">
          <p className="mb-1 text-center text-[12px] text-[var(--ink-muted)]">
            Or add an account by hand with this key
          </p>
          <p className="break-all text-center font-mono text-[13px] font-semibold tracking-wider text-[var(--ink-primary)]">
            {key}
          </p>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowKey(true)}
          className="w-full text-center text-[12px] text-[var(--ink-muted)] underline hover:text-[var(--ink-primary)]"
        >
          Can't scan it? Enter a key instead
        </button>
      )}
    </div>
  );
}
